import { AlertTriangle } from "lucide-react";
import { useState } from "react";

import { type TunnelRow, type TunnelTokenCreate, tokenExpiry, useRotateTunnelToken } from "@/client/backend/tunnels";
import { keyLifetimeOptions } from "@/client/backend/api-key";
import { CopyButton } from "@/components/copy";
import { ConnectorCommand } from "@/components/network/connector-command";
import { FormError, SubmitButton } from "@/components/network/form";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { formatDateTime } from "@/utils/format";

export function TokenExpirationField({ idPrefix, disabled }: { idPrefix: string; disabled?: boolean }) {
	return (
		<div className="space-y-2">
			<Label htmlFor={`${idPrefix}-expiration`}>Token expires in</Label>
			<Select name="tokenExpiration" defaultValue="12" disabled={disabled}>
				<SelectTrigger id={`${idPrefix}-expiration`}>
					<SelectValue />
				</SelectTrigger>
				<SelectContent>
					{keyLifetimeOptions.map((option) => (
						<SelectItem key={option.months} value={option.months}>
							{option.label}
						</SelectItem>
					))}
				</SelectContent>
			</Select>
		</div>
	);
}

export function tokenFromForm(data: FormData): TunnelTokenCreate {
	return { expirationMonths: Number.parseInt(String(data.get("tokenExpiration") ?? "12"), 10) };
}

function TokenExpiry({ expiresAt }: { expiresAt?: string }) {
	if (!expiresAt) {
		return <span className="text-xs text-muted-foreground">No token</span>;
	}
	const { expired, soon, days } = tokenExpiry(expiresAt);
	const text = expired ? "Expired" : soon ? `Expires in ${days === 0 ? "under a day" : `${days}d`}` : "Expires";
	return (
		<span
			className={cn("inline-flex items-center gap-1 text-xs", expired || soon ? "text-warning" : "text-muted-foreground")}
			title={formatDateTime(expiresAt)}
		>
			{(expired || soon) && <AlertTriangle aria-hidden className="size-3.5" />}
			{text}
			{!expired && !soon && ` ${new Date(expiresAt).toLocaleDateString()}`}
		</span>
	);
}

function RotateTokenDialog({
	organizationId,
	tunnel,
	open,
	onOpenChange,
}: {
	organizationId: string;
	tunnel: TunnelRow;
	open: boolean;
	onOpenChange: (open: boolean) => void;
}) {
	const [value, setValue] = useState<string>();
	const [error, setError] = useState<string>();
	const { trigger: rotate, isMutating } = useRotateTunnelToken(organizationId);
	const replacing = tunnel.tokenExpiresAt !== undefined;
	// Done closes through here too: Radix reports only its own closes, so
	// the next open would otherwise show this token again.
	function changeOpen(next: boolean) {
		onOpenChange(next);
		if (!next) {
			setValue(undefined);
			setError(undefined);
		}
	}
	return (
		<Dialog open={open} onOpenChange={changeOpen}>
			{/* The token is shown once, so a click outside never dismisses it. */}
			<DialogContent
				className="sm:max-w-lg"
				onInteractOutside={(event) => {
					if (value) event.preventDefault();
				}}
			>
				<DialogHeader>
					<DialogTitle>{replacing && !value ? `Rotate the token of ${tunnel.name}` : `New token for ${tunnel.name}`}</DialogTitle>
					<DialogDescription>
						{value
							? "Run this on each connector host."
							: replacing
								? "The current token stops working at once, and its connectors exit within seconds. Run the new command to bring the tunnel back."
								: "A token runs this tunnel and nothing else."}
					</DialogDescription>
				</DialogHeader>
				{value ? (
					<>
						<ConnectorCommand tokenValue={value} />
						<DialogFooter>
							<CopyButton value={value} label="Copy the tunnel token" text="Copy token" />
							<Button onClick={() => changeOpen(false)}>Done</Button>
						</DialogFooter>
					</>
				) : (
					<form
						className="space-y-4"
						onSubmit={async (event) => {
							event.preventDefault();
							setError(undefined);
							const response = await rotate({ id: tunnel.id, token: tokenFromForm(new FormData(event.currentTarget)) });
							if (!response || response.error) {
								setError(response?.error?.message ?? "The token could not be issued.");
								return;
							}
							setValue(response.data.token);
						}}
					>
						<div className="sm:w-40">
							<TokenExpirationField idPrefix="rotate-token" disabled={isMutating} />
						</div>
						<FormError error={error} />
						<DialogFooter>
							<SubmitButton pending={isMutating}>{replacing ? "Rotate token" : "Create token"}</SubmitButton>
						</DialogFooter>
					</form>
				)}
			</DialogContent>
		</Dialog>
	);
}

// TunnelToken shows when a tunnel's token expires and lets an admin issue a
// new one, which revokes the old.
export function TunnelToken({
	organizationId,
	tunnel,
	canManage,
}: {
	organizationId: string;
	tunnel: TunnelRow;
	canManage: boolean;
}) {
	const [rotating, setRotating] = useState(false);
	const verb = tunnel.tokenExpiresAt ? "Rotate" : "Create";
	return (
		<div className="flex items-center gap-1">
			<TokenExpiry expiresAt={tunnel.tokenExpiresAt} />
			{canManage && (
				<>
					<Button
						variant="ghost"
						size="sm"
						className="-my-1 h-6 px-1.5 text-xs"
						aria-label={`${verb} the token of ${tunnel.name}`}
						onClick={() => setRotating(true)}
					>
						{verb}
					</Button>
					<RotateTokenDialog organizationId={organizationId} tunnel={tunnel} open={rotating} onOpenChange={setRotating} />
				</>
			)}
		</div>
	);
}
